import { useEffect, useMemo } from 'react';
import { Alert, Box } from '@mui/material';
import FB from 'assets/images/facebook.png';
import LI from 'assets/images/linkedin.png';
import LOGO from 'assets/images/nautiqus-logo.jpg';
import UE from 'assets/images/UE.png';
import axios from 'axios';
import { useAuth } from 'contexts/authContext';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Location,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { reactPaths } from 'root/pathnames';
import { loginAlertsMap } from './const';
import * as S from './LoginScreen.css';
import { PasswordExpiredForm } from './PasswordExpiredForm';

type FormData = {
  username: string;
  password: string;
};

export const LoginScreen = () => {
  const { user, checkAuth, signOut } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const location = useLocation();

  const from =
    (location.state as null | { from?: Location })?.from?.pathname ||
    reactPaths.home;

  const { t } = useTranslation(['login']);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ mode: 'onTouched' });

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    const body = new URLSearchParams(Object.entries(data)).toString();
    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      credentials: 'same-origin',
    };

    const response = await axios.post('/backend/login.do', body, config);

    const responseURL = new URL(response.headers.location);
    const responsePathname = responseURL.pathname;
    const responseParams = Array.from(responseURL.searchParams.keys());

    const alertValue =
      responsePathname === '/loginfailed' ? 'failed' : responseParams[0];
    if (alertValue) {
      const isPasswordExpired = alertValue === 'password_expired';
      setSearchParams({ alert: alertValue }, { replace: !isPasswordExpired });
      return;
    }
    checkAuth({ successCallback: () => navigate(from, { replace: true }) });
  };

  const isActionLogout = searchParams.get('action') === 'logout';
  const isPasswordExpired = searchParams.get('alert') === 'password_expired';

  const currentYear = new Date().getFullYear();

  const alertData = useMemo(() => {
    const searchParam = searchParams.get('alert');
    if (searchParam) {
      return loginAlertsMap[searchParam];
    }
    return undefined;
  }, [searchParams]);

  useEffect(() => {
    if (isActionLogout) {
      user && signOut();
      setSearchParams({ alert: 'logout' }, { replace: true });
    }
  }, [isActionLogout, user, signOut, setSearchParams]);

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        px: 2,
        pb: 4,
      }}
    >
      <S.LogoContainer>
        <img src={LOGO} alt='logo' />
      </S.LogoContainer>
      <S.Title>{t('info', { ns: 'login' })}</S.Title>
      {!!alertData && (
        <Alert
          severity={alertData.alertVariant}
          sx={{ whiteSpace: 'pre-line' }}
        >
          {alertData.alertContent}
        </Alert>
      )}
      {isPasswordExpired ? (
        <PasswordExpiredForm />
      ) : (
        <S.Form onSubmit={handleSubmit(onSubmit)}>
          <S.FieldGroup>
            <div>
              <S.Input
                {...register('username', { required: true })}
                name='username'
                autoComplete='username'
                variant='outlined'
                size='small'
                label={t('userPlaceholder', { ns: 'login' })}
              />
              {errors.username && (
                <S.Error>{t('fieldRequired', { ns: 'login' })}</S.Error>
              )}
            </div>
          </S.FieldGroup>
          <S.FieldGroup>
            <div>
              <S.Input
                {...register('password', { required: true })}
                type='password'
                name='password'
                autoComplete='current-password'
                variant='outlined'
                size='small'
                label={t('passwordPlaceholder', { ns: 'login' })}
              />
              {errors.password && (
                <S.Error>{t('fieldRequired', { ns: 'login' })}</S.Error>
              )}
            </div>
          </S.FieldGroup>
          <S.LoginButton
            disabled={!!errors.password || !!errors.username}
            type='submit'
          >
            {t('signin', { ns: 'login' })}
          </S.LoginButton>
        </S.Form>
      )}
      <S.Socials>
        <a href='https://www.facebook.com/Nautiqus'>
          <img src={FB} alt='facebook' />
        </a>
        <a href='https://www.linkedin.com/company/10800216'>
          <img src={LI} alt='linkedin' />
        </a>
      </S.Socials>
      <S.Banner>
        <img src={UE} alt='ue' />
        <p>
          Nautiqus freight forwarding software. © {currentYear} Jit Team.
          Wszelkie prawa zastrzeżone
        </p>
      </S.Banner>
    </Box>
  );
};
